import { Accordion, Anchor } from '@mantine/core';
import { DateTime } from 'luxon';
import { useIntl } from 'react-intl';

import { shipmentSummarySectionMessages } from './shipment-summary-section-messages';
import styles from './ShipmentSummarySection.module.css';
import { useFreightTerminalData } from '../../../../hooks/use-freight-terminal-data';
import { resolveChargeLabel, selectShipmentCharges } from '../../../../ltl/charge-labels';
import { getCarrierInitials } from '../../../freight-rate-card/get-carrier-initials';
import { getCarrierLogoSrc } from '../../../freight-rate-card/get-carrier-logo-src';
import { DetailSection } from '../detail-section/DetailSection';
import { TerminalCard } from '../terminal-card/TerminalCard';

import type { FreightTerminalAddress } from '../../../../types/freight-responses';
import type { LtlShipment } from '../../../../types/ltl';

type ShipmentSummarySectionProps = {
  shipment: LtlShipment;
  currencyCode?: string;
};

const DEFAULT_CURRENCY_CODE = 'USD';

const formatDate = (isoDate: string): string => {
  if (!isoDate) return '';
  const parsed = DateTime.fromISO(isoDate);
  return parsed.isValid ? parsed.toLocaleString(DateTime.DATE_MED) : isoDate;
};

const formatTerminalCityState = (address: FreightTerminalAddress | null | undefined): string | null => {
  if (!address) return null;
  const cityState = [address.city, address.state].filter(Boolean).join(', ');
  return cityState !== '' ? cityState : null;
};

/**
 * Right-column Shipment Summary card (§6.6 / §7.6). Carrier row with an
 * initials logo box, a Cost Breakdown accordion (formatted total + LTL
 * disclaimer), and a static Invoice details block. Store-agnostic.
 */
export const ShipmentSummarySection = ({ shipment, currencyCode = DEFAULT_CURRENCY_CODE }: ShipmentSummarySectionProps) => {
  const intl = useIntl();

  const arrivesDate = formatDate(shipment.estimatedDeliveryDate);
  const formattedTotal = intl.formatNumber(shipment.totalCharges, { style: 'currency', currency: currencyCode });
  const charges = selectShipmentCharges(shipment);
  const { originTerminalCode, destinationTerminalCode } = shipment;
  const hasTerminalData = originTerminalCode != null && destinationTerminalCode != null;

  const { data: terminalData } = useFreightTerminalData({
    originCode: originTerminalCode ?? null,
    destinationCode: destinationTerminalCode ?? null,
    scac: shipment.carrierScac,
    sellerFreightProviderId: shipment.sellerFreightProviderId,
  });

  const logoSrc = getCarrierLogoSrc(shipment.carrierScac);
  const logo = logoSrc ? (
    <img alt={shipment.carrierName} className={styles.logoImage} src={logoSrc} />
  ) : (
    <span className={styles.logoInitials}>{getCarrierInitials(shipment.carrierName)}</span>
  );

  return (
    <DetailSection testId='shipment-summary-section' title={intl.formatMessage(shipmentSummarySectionMessages.sectionTitle)}>
      <div className={styles.carrierRow}>
        <div className={styles.carrierHeader}>
          <div className={styles.logoBox}>{logo}</div>
          <div className={styles.carrierInfo}>
            <span className={styles.carrierName}>
              {intl.formatMessage(shipmentSummarySectionMessages.carrierService, {
                carrier: shipment.carrierName,
                service: shipment.serviceName,
              })}
            </span>
            {arrivesDate !== '' && (
              <span className={styles.arrives}>{intl.formatMessage(shipmentSummarySectionMessages.arrives, { date: arrivesDate })}</span>
            )}
          </div>
        </div>

        {hasTerminalData && (
          <div className={styles.terminalsGrid}>
            <TerminalCard
              cityState={formatTerminalCityState(terminalData?.origin?.address)}
              code={originTerminalCode}
              label={intl.formatMessage(shipmentSummarySectionMessages.pickupTerminalLabel)}
            />
            <TerminalCard
              cityState={formatTerminalCityState(terminalData?.destination?.address)}
              code={destinationTerminalCode}
              label={intl.formatMessage(shipmentSummarySectionMessages.deliveryTerminalLabel)}
            />
          </div>
        )}
      </div>

      <Accordion variant='contained'>
        <Accordion.Item value='cost-breakdown'>
          <Accordion.Control>{intl.formatMessage(shipmentSummarySectionMessages.costBreakdown)}</Accordion.Control>
          <Accordion.Panel>
            {charges.length > 0 && (
              <div className={styles.chargesList}>
                {charges.map((charge, index) => (
                  <div className={styles.chargeRow} data-testid={`cost-breakdown-charge-${charge.code}`} key={`${charge.code}-${index}`}>
                    <span className={styles.chargeLabel}>{resolveChargeLabel(charge, intl)}</span>
                    <span className={styles.chargeAmount}>
                      {intl.formatNumber(charge.price, { style: 'currency', currency: currencyCode })}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <div className={styles.totalRow}>
              <span className={styles.totalLabel}>{intl.formatMessage(shipmentSummarySectionMessages.total)}</span>
              <span className={styles.totalAmount}>{formattedTotal}</span>
            </div>
            <span className={styles.disclaimer}>{intl.formatMessage(shipmentSummarySectionMessages.costDisclaimer)}</span>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>

      <div className={styles.invoice}>
        <span className={styles.invoiceCopy}>{intl.formatMessage(shipmentSummarySectionMessages.invoiceCopy)}</span>
        {/* Placeholder href: real billing destination is wired by the host later. */}
        <Anchor className={styles.manageBilling} href='#' rel='noopener noreferrer' target='_blank'>
          {intl.formatMessage(shipmentSummarySectionMessages.manageBilling)}
        </Anchor>
      </div>
    </DetailSection>
  );
};
